import {until} from 'lit-html/directives/until';
import {repeat} from 'lit-html/directives/repeat';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';
import {recommendedActivityType, products} from '@modules/constants-enumerate';
import '@modules/ems-collapse';
import {LoadingEllipses} from '@epg/epg-components';
import {dashboardLabels, generalLabels} from '@modules/i18n-labels';
import Materials from  '!svg-inline-loader!./assets/materials.svg';
import Vocabulary from  '!svg-inline-loader!./assets/vocabulary.svg';
import Activity from  '!svg-inline-loader!./assets/activity.svg';
import ExitTicket from  '!svg-inline-loader!./assets/exit_ticket.svg';

const dynamicCss = [
  '.ai-English-Support',
  '.ai-Social-Emotional',
  '.border-dashed-bottom',
  '.collapse-els',
  '.collapse-ses',
  '.btn-english_language_supports',
  '.btn-social_emotional_supports',
  '.ql-indent-1',
  '.ai-vocabulary',
  ',ai-activity',
  '.ai-exit_ticket',
  '.btn-success_strategies',
  '.btn-english_learner_supports',
  '.field-english_language_supports',
  '.field-english_learner_supports'
];

const iconMap = {
  english_language_supports: 'ai-English-Support',
  social_emotional_supports: 'ai-Social-Emotional',
  english_learner_supports: 'ai-English-Support',
};
const acscdn = (path) => 'https://educate.aofl.com/acscdn' + path;

const getTranslation = (field, ctx) => {
  const translations = {
    materials: ctx.i18n.__('<tt-NKFF5SRA>', 'Materials'),
    mathematical_practices: ctx.i18n.__('<tt-XqEkGnjq>', 'Mathematical Practices'),
    try_it: ctx.i18n.__('<tt-uXG8fYv6>', 'Try It!'),
    set_up: ctx.i18n.__('<tt-Bm3gCyFu>', 'Set Up'),
    learner_reflection: ctx.i18n.__('<tt-WJC6IrRo>', 'Learner Reflection'),
    english_language_supports: ctx.i18n.__('<tt-tgEnP+Jl>', 'English Language Supports'),
    social_emotional_supports: ctx.i18n.__('<tt-umuk9Hxs>', 'Success Skills'),
    purpose: ctx.i18n.__('<tt-mErCm7V/>', 'Purpose'),
    vocabulary: ctx.i18n.__('<tt-3Qyo7Qq3>', 'Vocabulary'),
    activity: ctx.i18n.__('<tt-L4kh4Ait>', 'Activity'),
    exit_ticket: ctx.i18n.__('<tt-l50/W5hE>', 'Exit Ticket'),
    success_strategies: ctx.i18n.__('<tt-IVF+HLVr>', 'Success Strategies'),
    teacher: ctx.i18n.__('<tt-5xdHzcgG>', 'Teacher'),
    student: ctx.i18n.__('<tt-6jL59D17>', 'Student'),
    english_learner_supports: ctx.i18n.__('<tt-i6lCbv1B>', 'English Learner Support'),
  };
    return translations[field];
};
const headerIconMapping = (field) => {
  const mapping = {
    vocabulary: Vocabulary,
    activity: Activity,
    exit_ticket: ExitTicket
  };

  return mapping[field];
};
const getImgURL = (product) => {
  const urls = {
    [products.MATH_ACADEMY]: 'https://educate.aofl.com/cdn/images/icon/mathmastery.png',
    [products.MY_MATH_ACADEMY]: 'https://educate.aofl.com/cdn/images/icon/my_math_academy.png',
    [products.MY_READING_ACADEMY]: 'https://educate.aofl.com/cdn/images/icon/my_reading_academy.png',
    [products.MY_READING_ACADEMY_ES]: 'https://educate.aofl.com/cdn/images/icon/my_reading_academy_es.png',
  };
  return urls[product];
};
const getAudienceSelectButtons = (ctx, html) => {
  return [recommendedActivityType.ONE_ON_ONE, recommendedActivityType.GROUP].map((type) => {
    if (ctx.recommendedActivityTypes[type]) {
      return html`
        <label
          class="btn px-4 cursor-pointer ${ctx.activeRecommendedActivity.type === type ? 'selected btn-primary' : 'btn-outline-light'}"
          for="option-${type}">
          <input type="radio" name="options" id="option-${type}" value="${type}">
          ${type === recommendedActivityType.ONE_ON_ONE ? until(ctx.__('<tt-m/Gmshmo>', 'One-on-One Activity')) : until(ctx.__('<tt-HpaG48EI>', 'Group Activity'))}
        </label>
      `;
    }
  });
};
const getField = (ctx, html, field, iconName, enableBorderDash = false) => {
  if (ctx.activeRecommendedActivity?.[field]) {
    let icon = html``;
    if (iconName) {
      if (ctx.activeRecommendedActivity?.version === '2')  {
        icon = html`<span aria-label="${until(getTranslation(field, ctx))}" class="header-icon ai-${field}">${html([headerIconMapping(field)])}</span>`;
      } else {
        icon = html`<span aria-label="${until(getTranslation(field, ctx))}" class="ai ${iconName} subtitle-icon mr-2 align-middle font-size-lg"></span>`;
      }
    }

    return html`
      <div class="${(enableBorderDash) ? 'border-dashed-bottom pb-4' : ''} mb-4">
        <div class="mb-2 content-icon">
          ${icon}
          <span
            class="align-middle subtitle font-size-lg font-weight-bold outline-black">${until(getTranslation(field, ctx))}</span>
        </div>
        <div class="content font-size-small">
          ${unsafeHTML(ctx.activeRecommendedActivity[field])}
        </div>
      </div>
    `;
  }
};

const getCollapse = (field, tabIndex, ctx, html) => {
  if (!ctx.activeRecommendedActivity?.[field]) {
    return;
  }
  const collapseHeader = html`
    <span aria-label="${until(getTranslation(field, ctx))}" class="ai ${iconMap[field]} field-${field} align-middle font-size-lg mr-3"></span>
    <span class="align-middle collapse-label">${until(getTranslation(field, ctx))}</span>
  `;
  return html`
    <div class="collapse-wrapper ${ctx.collapseOpen[field] ? 'open' : ''}">
      <button
        type="button"
        class="btn-collapse w-100 cursor-pointer py-4 px-3 font-weight-semibold border rounded btn-${field}"
        @click="${(e) => ctx.toggleCollapse(field)}"
        tabindex="0"
        aria-expanded="${ctx.collapseOpen[field]}"
        data-bi-id="${field}-collapse-${ctx.activeRecommendedActivity.id}"
        >
        <div class="d-flex justify-content-between align-items-center">
          <div>
            ${collapseHeader}
          </div>
          <span class="ai ai-Chevron_Black ${ctx.collapseOpen[field] ? 'rotate-90' : ''}"></span>
        </div>
      </button>

      <div class="mb-4 collapse-content">
        <div class="px-4 py-4 bg-light rounded-bottom content font-size-small">
          ${unsafeHTML(ctx.activeRecommendedActivity[field])}
        </div>
      </div>
    </div>
  `;
};
const getPrintButton = (ctx, html) => {
  if (ctx.showPdfButton[ctx.activeRecommendedActivity.id]) {
    return html`
      <div class="print-hidden">
        <a class="btn text-body" href="${acscdn('/catalog' + ctx.activeRecommendedActivity.generated_pdf.full_path)}"
           download="${ctx.activeRecommendedActivity.generated_pdf.filename}.pdf" target="_blank"
           data-bi-id="print-activity-${ctx.activeRecommendedActivity.id}"
        >
          <img alt="Print and download button" src="${require('@assets/images/printer-icon.webp')}" width="25"/>
          ${until(ctx.__('<tt-cfZ316oV>', 'Print/Download'))}
        </a>
      </div>`;
  }
};

const getMaterialsPdfs = (ctx, html) => {
  if (!ctx.activeRecommendedActivity?.materials_pdfs) {
    return;
  }

  if (ctx.activeRecommendedActivity?.version === '2') {
    return html`
      <ul>
        ${repeat(ctx.activeRecommendedActivity.materials_pdfs, (pdf) => pdf, (pdf) => html`
          ${pdf?.full_path ? html`
            <li>
              ${pdf.filename}
              <a href="${acscdn('/catalog' + pdf.full_path)}" download="${pdf.filename}"
                 data-bi-id="print-activity-${ctx.activeRecommendedActivity.id}-material-pdf"
                 data-bi-value="${pdf.filename}"
                 target="_blank">${until(ctx.__('<tt-F6bR3fCb>', '[PDF]'))}</a>
            </li>
          ` : html``}
        `)}
      </ul>
    `;
  } else {
    return html`
    <ul class="materials-pdf-list">
    ${repeat(ctx.activeRecommendedActivity.materials_pdfs, (pdf) => pdf, (pdf) => html`
      ${pdf?.full_path ? html`
        <li>
          <a class="d-block material-pdf-item" href="${acscdn('/catalog' + pdf.full_path)}" download="${pdf.filename}"
             target="_blank">
            <img alt="pdf" src="${require('@assets/images/pdf.webp')}" alt="" width="25"/>
            <div class="d-inline-block text-body">${pdf.filename}.pdf</div>
            <span class="text-blue">(${until(generalLabels.download)})</span>
          </a>
        </li>
      ` : html``}
    `)}
    </ul>
  `;
  }
};

const getMaterials = (ctx, html) => {
  if (!ctx.activeRecommendedActivity.materials) {
    return;
  }
  return html`
    <div class="pb-4 mb-4 border-dashed-bottom">
      <div class="mb-2 d-flex align-items-center">
        ${ctx.activeRecommendedActivity?.version === '2' ? html`
          <span aria-label="${until(getTranslation('materials', ctx))}" class="header-icon ai-materials mr-3">${html([Materials])}</span>
        ` : html``}
        <span class="align-middle subtitle font-size-lg font-weight-bold">${until(getTranslation('materials', ctx))}</span>
      </div>
      <div class="content font-size-small">
        ${unsafeHTML(ctx.activeRecommendedActivity?.materials)}
        ${getMaterialsPdfs(ctx, html)}
      </div>
    </div>`;
};

const getPurpose = (ctx, html) => {
  if (!ctx.activeRecommendedActivity.purpose) {
    return;
  }
  return html`
    <div class="pb-4">
        <span class="font-weight-bold font-size-lg">${until(getTranslation('purpose', ctx))}</span>
        ${unsafeHTML(ctx.activeRecommendedActivity?.purpose)}
    </div>`;
};

const getSuccessStrategies = (ctx, html, field) => {
  if (!ctx.activeRecommendedActivity?.success_strategies_teacher || !ctx.activeRecommendedActivity?.success_strategies_student) {
    return;
  }

  const collapseHeader = html`
    <span aria-label="${until(getTranslation(field, ctx))}" class="ai ai-target align-middle mr-3 font-size-small"></span>
    <span class="align-middle collapse-label font-size-small">${until(getTranslation(field, ctx))}</span>
  `;
  return html`
    <div class="collapse-wrapper ${ctx.collapseOpen[field] ? 'open' : ''}">
      <div
        class="btn-collapse cursor-pointer py-4 px-3 font-size-small font-weight-semibold border rounded btn-${field}"
        @click="${() => ctx.toggleCollapse(field)}"
        data-bi-id="success-strategies-collapse-${ctx.activeRecommendedActivity.id}"
        aria-expanded="${ctx.collapseOpen[field]}">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            ${collapseHeader}
          </div>
          <span class="ai ai-Chevron_Black ${ctx.collapseOpen[field] ? 'rotate-90' : ''}"></span>
        </div>
      </div>

      <div class="mb-4 collapse-content">
        <div class="px-4 py-4 bg-light rounded-bottom content font-size-small">
          ${ctx.activeRecommendedActivity?.success_strategies_teacher ? html`
            <div class="font-weight-bold font-size-small">${until(getTranslation('teacher', ctx))}</div>
            ${unsafeHTML(ctx.activeRecommendedActivity?.success_strategies_teacher)}
          ` : html``}
          ${ctx.activeRecommendedActivity?.success_strategies_student ? html`
            <div class="font-weight-bold font-size-small">${until(getTranslation('student', ctx))}</div>
            ${unsafeHTML(ctx.activeRecommendedActivity?.success_strategies_student)}
          ` : html``}
        </div>
      </div>
    </div>
  `;
};

const version1 = (ctx, html) => html`
  <div class="d-flex flex-column flex-lg-row">
    <div class="flex-1 px-4">
      ${getMaterials(ctx, html)}
      ${getField(ctx, html, 'set_up')}
      ${getField(ctx, html, 'try_it', 'ai-Try-It')}
      ${getField(ctx, html, 'learner_reflection', 'ai-Social-Emotional')}
    </div>
    <div class="flex-1 px-4">
      ${getField(ctx, html, 'mathematical_practices', 'ai-Math-Practices')}
      ${getCollapse('english_language_supports', 1, ctx, html)}
      ${getCollapse('social_emotional_supports', 2, ctx, html)}
    </div>
  </div>
`;

const version2 = (ctx, html) => html`
  ${getPurpose(ctx, html)}
  <div class="d-flex flex-column flex-lg-row">
    <div class="flex-1 mr-5">
      ${getMaterials(ctx, html)}
      ${getField(ctx, html, 'vocabulary', 'ai-Try-It', true)}
      ${getField(ctx, html, 'activity', 'ai-Try-It' )}
    </div>
    <div class="flex-1">
      ${getField(ctx, html, 'exit_ticket', 'ai-Try-It')}
      ${getSuccessStrategies(ctx, html, 'success_strategies')}
      ${getCollapse('english_learner_supports', 2, ctx, html)}
    </div>
  </div>
`;

export default (ctx, html) => {
  if (!ctx.activities.length) {
    return html`<loading-ellipses></loading-ellipses>`;
  }
  return html`
  <div class="border-bottom print-hidden">
    ${(ctx.forGroupSupport) ? html`
      <div class="text-center h4 w-100 mx-auto my-4 font-size-lg font-weight-semibold">
        ${until(dashboardLabels.recommendedActivityName(ctx.activeRecommendedActivity?.activity_name))}
        <span class="font-weight-normal font-size-small">(${until(dashboardLabels.estimatedTime(ctx.activeRecommendedActivity?.estimated_time))})</span>
      </div>
        <div class="d-flex justify-content-center align-items-center flex-row mb-4">
          <div class="h4 mb-0 mr-2">${until(dashboardLabels.INSTRUCTION_TYPES)}:</div>
          <div class="btn-group btn-group-toggle" role="group" @change="${ctx.changeAudience}">
            ${getAudienceSelectButtons(ctx, html)}
          </div>
        </div>
      </div>
      <div id="print-content" class="position-relative">
        ${ctx.activeRecommendedActivity?.version === '2' ? html`` : html`
          <div class="font-size-sm">${ctx.activeRecommendedActivity?.activity_description}</div>`}
        ${ctx.activeRecommendedActivity?.version === '2' ? version2(ctx, html) : version1(ctx, html)}
      </div>
    ` : html`
      <div class="text-center font-italic title w-50 mx-auto font-weight-semibold">${ctx.skillName}</div>
      <h2 class="font-weight-semibold mb-3">${until(ctx.__('<tt-lSXs2C+P>', 'Recommended Activity'))}</h2>
      <div class="d-flex justify-content-between flex-row mb-4">
        <div class="${ctx.forStudentView ? 'd-none' : ''}">
          <div class="d-inline-block mr-2 align-middle">${until(dashboardLabels.INSTRUCTION_TYPES)}:</div>
          <div class="btn-group btn-group-toggle align-middle" role="group" @change="${ctx.changeAudience}">
            ${getAudienceSelectButtons(ctx, html)}
          </div>
        </div>
        <div class="grow"></div>
        ${getPrintButton(ctx, html)}
      </div>
    </div>
    <div id="print-content" class="position-relative">
      <div class="mb-2 d-flex align-items-center">
          <img src="${getImgURL(ctx.activeRecommendedActivity?.product || '')}" class="mr-3"
               width="60">
          <h2 class="font-weight-bold font-size-lg mr-2 d-inline-block">${until(dashboardLabels.recommendedActivityName(ctx.activeRecommendedActivity?.activity_name))}<br/>
            <span class="font-weight-normal font-size-small">(${until(dashboardLabels.estimatedTime(ctx.activeRecommendedActivity?.estimated_time))})</span>
          </h2>
      </div>
      ${ctx.activeRecommendedActivity?.version === '2' ? html`` : html`
        <div class="font-size-sm">${ctx.activeRecommendedActivity?.activity_description}</div>`}
      ${ctx.activeRecommendedActivity?.version === '2' ? version2(ctx, html) : version1(ctx, html)}
    </div>
  `}
`;
};
